import type { Coach as CoachModel } from '../model/Coach';
import type { Division, PlayoffStructure, PostseasonSeedSnapshotRow, Week } from '../model/Season';
import { computePlayoffSeeds } from './playoffBracketBuild';
import { getStandingsThroughWeek, isRegularSeasonComplete, lastRegularSeasonWeekIndex } from './seasonStandingsCore';

export type SeasonDataForPostseasonLock = {
    divisions: Division[];
    weeks: Week[];
    playoffStructure?: PlayoffStructure;
};

export function validPlayoffStructureMinimal(ps: PlayoffStructure | undefined): ps is PlayoffStructure {
    return !!(
        ps &&
        typeof ps.divisionWinnersPerDivision === 'number' &&
        typeof ps.wildcardCount === 'number' &&
        typeof ps.byeCount === 'number'
    );
}

export function canFinalizeRegularSeasonForPlayoffs(seasonData: SeasonDataForPostseasonLock): boolean {
    return validPlayoffStructureMinimal(seasonData.playoffStructure) && isRegularSeasonComplete(seasonData.weeks);
}

/**
 * Read optional postseason lock fields from parsed season JSON (API or import).
 */
export function parsePostseasonLockFromSeasonJson(parsed: unknown): {
    postseasonSeedsSnapshot?: PostseasonSeedSnapshotRow[];
    postseasonSeededAt?: string;
    postseasonLastRegularWeekIndex?: number;
} {
    if (!parsed || typeof parsed !== 'object') return {};
    const o = parsed as Record<string, unknown>;
    const snap = o.postseasonSeedsSnapshot;
    let postseasonSeedsSnapshot: PostseasonSeedSnapshotRow[] | undefined;
    if (Array.isArray(snap)) {
        const rows: PostseasonSeedSnapshotRow[] = [];
        for (const item of snap) {
            if (!item || typeof item !== 'object') continue;
            const r = item as Record<string, unknown>;
            if (
                typeof r.seed === 'number' &&
                typeof r.teamId === 'string' &&
                typeof r.teamName === 'string' &&
                typeof r.coachName === 'string'
            ) {
                rows.push({
                    seed: r.seed,
                    teamId: r.teamId,
                    teamName: r.teamName,
                    coachName: r.coachName
                });
            }
        }
        if (rows.length > 0) postseasonSeedsSnapshot = rows;
    }
    const postseasonSeededAt = typeof o.postseasonSeededAt === 'string' ? o.postseasonSeededAt : undefined;
    const idx = o.postseasonLastRegularWeekIndex;
    const postseasonLastRegularWeekIndex =
        typeof idx === 'number' && Number.isInteger(idx) && idx >= 0 ? idx : undefined;
    return { postseasonSeedsSnapshot, postseasonSeededAt, postseasonLastRegularWeekIndex };
}

export function buildPostseasonLock(
    seasonData: SeasonDataForPostseasonLock,
    leagueCoaches: CoachModel[]
): {
    postseasonSeedsSnapshot: PostseasonSeedSnapshotRow[];
    postseasonSeededAt: string;
    postseasonLastRegularWeekIndex: number;
} {
    if (!canFinalizeRegularSeasonForPlayoffs(seasonData)) {
        throw new Error('Regular season is not complete or playoff structure is not configured.');
    }
    const ps = seasonData.playoffStructure!;
    const lastRs = lastRegularSeasonWeekIndex(seasonData.weeks);
    if (lastRs < 0) {
        throw new Error('No regular season week found to seed playoffs.');
    }
    const standings = getStandingsThroughWeek(seasonData, leagueCoaches, lastRs);
    const allTeamsFlat = Object.values(standings).flat();
    const rows = computePlayoffSeeds(ps, standings, allTeamsFlat);
    return {
        postseasonSeedsSnapshot: rows.map(r => ({ ...r })),
        postseasonSeededAt: new Date().toISOString(),
        postseasonLastRegularWeekIndex: lastRs
    };
}

export function isUsablePostseasonSnapshot(rows: PostseasonSeedSnapshotRow[] | undefined | null): rows is PostseasonSeedSnapshotRow[] {
    return Array.isArray(rows) && rows.length > 0;
}
